/**
 * **正在连接页。**
 *
 * 评审：「应当加一个"正在连接"页面，无论是正在加入游戏还是正在创建房间都进入
 * 这个界面，若出现网络问题就返回之前的页面。」
 *
 * ## 它为什么必须是一个**屏幕**，而不是一句提示
 *
 * 连接是要花时间的（探中继 + 等对端，实测一秒到十几秒），而这段时间里
 * **房间并不存在**。以前这段等待是隐形的：界面直接进了房间页，于是
 * "还没连上"看起来和"已经开好了"一模一样。把人放进一个不存在的房间里，
 * 比让他多看两秒等待页糟得多。
 *
 * 所以这里守的是一条纪律：**房间页只在 `ready` 之后才进**。
 *
 * ## 两种失败，两句话
 *
 * | 卡在哪 | 玩家看到 |
 * | --- | --- |
 * | 中继连不上（`probing` 失败） | "…一个信令中继都没连上" —— 网络问题 |
 * | 房间里没人应答（`searching` 超时） | "没有找到这个房间" —— 房间号/对方问题 |
 *
 * 这两句都由 `ui/connect.ts` 的状态机给（`reason`），这一屏只负责印出来
 * 并按相位换成不同的标题。
 *
 * ## 失败之后**自己退回去**
 *
 * 评审要的就是这个。但"立刻弹回去"会让人来不及看见发生了什么，
 * 所以停 `FAIL_HOLD_MS` 再退 —— 同时留一个「返回」，急着走的人不用等。
 *
 * ⚠️ 这一屏**一个字都不解释**（除了相位本身）：它是一次状态汇报，
 * 不是一个教程。理由写在 `gameTable.ts` 的「没有提示」那一节。
 */

import { Container } from 'pixi.js';
import {
  createColumn,
  richLabel,
  notice,
  pageChrome,
  panel,
  stampButton,
  uiPad,
  INK,
} from '../../render/ui';
import { bi, t } from '../../i18n';
import type { ConnectState } from '../connect';
import type { Screen, ScreenContext } from '../screen';
import { SCREEN } from './ids';

const PANEL_X = 320;
const PANEL_WIDTH = 640;

/**
 * 失败之后在这一页停多久再退回去。
 *
 * 2.6 秒是"够读完那一句理由、又不至于让人以为卡住了"的长度。
 * 它是**墙上时间**，和音乐、背景都没有关系。
 */
const FAIL_HOLD_MS = 2600;

/** 相位 → 这一屏中间那一行字。`failed` 用状态机给的理由 */
function phaseLine(state: ConnectState): string {
  switch (state.phase) {
    case 'probing':
      return t('connecting.probing');
    case 'searching':
      return t('connecting.searching');
    case 'ready':
      return t('connecting.ready');
    case 'failed':
      return state.reason ?? t('connecting.failed');
    case 'idle':
      return t('connecting.idle');
  }
}

export function createConnectingScreen(ctx: ScreenContext): Screen {
  const container = new Container();
  container.label = 'screen-connecting';
  let column = createColumn(1);
  let unsubscribe: (() => void) | null = null;
  let failTimer: ReturnType<typeof setTimeout> | null = null;
  let dirty = false;
  let destroyed = false;

  const build = (): void => {
    const scale = ctx.settings.get().uiScale;
    const { width, height } = ctx.bounds;
    const state = ctx.room.connect();

    column = createColumn(scale);
    container.addChild(column.container);
    column.addRaw(
      pageChrome({ scale, width, height, header: bi('connecting.header'), page: 'P.02b' }),
    );

    const sheet = panel({
      scale,
      width: PANEL_WIDTH,
      title: state.mode === 'create' ? bi('common.createRoom') : bi('connecting.joinPanel'),
      padding: 24,
    });
    sheet.container.position.set(uiPad(PANEL_X, scale), uiPad(240, scale));
    column.addRaw(sheet.container);

    const inner = createColumn(scale, 14);
    sheet.body.addChild(inner.container);
    const contentWidth = PANEL_WIDTH - uiPad(48, scale);

    inner.add(
      richLabel({
        text: t('connecting.roomCode', { code: state.code === '' ? '····' : '#' + state.code }),
        scale,
        size: 26,
        fill: INK.accent,
        role: 'display',
        tracking: 4,
      }),
    );

    inner.add(
      richLabel({
        text: phaseLine(state),
        scale,
        size: 15,
        fill: state.phase === 'failed' ? INK.danger : INK.text,
        role: 'cjk',
        wrapWidth: contentWidth,
        align: 'left',
      }),
    );

    if (state.phase === 'failed') {
      // 失败那一句已经在上面印过一遍了，这里只补一句"接下来会怎样"——
      // 不说的话，那 2.6 秒里玩家会以为要一直停在这一页
      inner.add(
        notice({
          text: t('connecting.returning'),
          scale,
          width: contentWidth,
          tone: 'warn',
        }),
      );
    }

    const back = stampButton({
      label: t(state.phase === 'failed' ? 'common.backPlain' : 'connecting.cancel'),
      scale,
      variant: 'quiet',
      onPress: () => leave(),
    });
    inner.add(back);

    sheet.fitContent(Math.round(inner.y) - uiPad(14, scale));
  };

  /**
   * 退回去。
   *
   * ⚠️ **`ready` 的时候不许 cancel** —— 那时候房间是好的，`cancelConnect()`
   * 会把刚连上的那一桌收掉。成功那条路走 `toRoom()`。
   */
  const leave = (): void => {
    if (ctx.room.connect().phase !== 'ready') ctx.room.cancelConnect();
    ctx.stack.pop();
  };

  /** 连上了：换掉这一屏，进房间 */
  const toRoom = (): void => {
    ctx.stack.pop();
    ctx.stack.push(SCREEN.room);
  };

  const rebuildSoon = (): void => {
    if (dirty) return;
    dirty = true;
    queueMicrotask(() => {
      dirty = false;
      if (destroyed) return;
      rebuildNow();
    });
  };

  const rebuildNow = (): void => {
    column.dispose();
    container.removeChildren();
    build();
  };

  /**
   * 连接状态变了。
   *
   * ⚠️ 两条出口都**只走一次**：`ready` 立刻进房间；`failed` 停一会儿再退。
   * 用一个 `settled` 闩着 —— 状态机可能连着报好几条（比如超时和
   * `onStatus` 一起来），不闩的话会 push 两次房间页。
   */
  let settled = false;
  const onState = (state: ConnectState): void => {
    if (settled) return;
    if (state.phase === 'ready') {
      settled = true;
      toRoom();
      return;
    }
    if (state.phase === 'failed') {
      settled = true;
      rebuildSoon();
      failTimer = setTimeout(() => {
        failTimer = null;
        if (!destroyed) leave();
      }, FAIL_HOLD_MS);
      return;
    }
    rebuildSoon();
  };

  build();

  return {
    container,
    onEnter() {
      unsubscribe = ctx.room.subscribeConnect((state) => onState(state));
      // 进来之前可能就已经连上了（比如探测飞快）—— 补一次
      onState(ctx.room.connect());
    },
    onExit() {
      unsubscribe?.();
      unsubscribe = null;
    },
    rebuild() {
      rebuildNow();
    },
    onKey(event) {
      if (event.key !== 'Escape') return false;
      leave();
      return true;
    },
    destroy() {
      destroyed = true;
      if (failTimer !== null) clearTimeout(failTimer);
      failTimer = null;
      unsubscribe?.();
      unsubscribe = null;
      column.dispose();
      container.destroy({ children: true });
    },
  };
}
